import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ProfileModel,
  RestaurantBranchEntity,
  RestaurantEntity,
} from 'src/app/swagger';
import { UserProfileService } from '../../services/user-profile/user-profile.service';
import { Role } from '../../enums/role.enum';
import { RestaurantsService } from '../../services/restaurants/restaurants.service';
import { switchMap, take } from 'rxjs';

export interface FiltersValue {
  restaurantId: string;
  restaurantBranchId: string;
}

@Component({
  selector: 'restaurant-branch-filter',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  standalone: false,
})
export class FiltersComponent implements OnInit, OnChanges {
  @Input() filterMode: 'restaurant' | 'branch' | 'both' = 'both';

  @Input() public restaurantId: string | null = '';
  @Input() public branchId: string | null = '';

  @Output() public filtersValueChange = new EventEmitter<FiltersValue>();

  public isAdmin = false;
  public isRestaurantAdmin = false;
  public isCustomer = false;

  private userProfile: ProfileModel | null = null;

  public restaurants: RestaurantEntity[] = [];
  public branches: RestaurantBranchEntity[] = [];
  private initialized = false;

  constructor(
    private userProfileService: UserProfileService,
    private restaurantsService: RestaurantsService
  ) {}

  public ngOnInit(): void {
    this.setUserProfile();
    this.setRestaurants();
    this.initialized = true;
  }
  
  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) return;
  
    if (changes['restaurantId']?.currentValue) {
      this.restaurantsService.setSelectedRestaurantId(this.restaurantId);
      this.setBranches();
    }
  
    if (changes['branchId']?.currentValue) {
      this.restaurantsService.setSelectedBranchId(this.branchId);
      this.emitFiltersValueChange(this.restaurantId || '', this.branchId || '');
    }
  }

  private setUserProfile() {
    this.userProfileService.getUserProfile().subscribe({
      next: (userProfile: ProfileModel) => {
        if (userProfile) {
          this.userProfile = userProfile;
          const userRoles = this.userProfile?.roles || [];
          this.isAdmin = userRoles.includes(Role.ADMINS);
          this.isRestaurantAdmin = userRoles.includes(Role.ADMINS);
          this.isCustomer = userRoles.includes(Role.USERS);
        }
      },
    });
  }

  private setRestaurants(): void {
    this.restaurants = [];

    this.restaurantsService
      .getRestaurants()
      .pipe(
        take(1),
        switchMap((restaurants) => {
          this.restaurants = restaurants;
          return this.restaurantsService
            .getSelectedRestaurantId()
            .pipe(take(1));
        })
      )
      .subscribe((restaurantId) => {
        this.restaurantId = restaurantId;
        this.restaurantsService.setSelectedRestaurantId(restaurantId);

        const selectedResto = this.restaurants.find(
          (r) => r.restaurantId === this.restaurantId
        );
        this.restaurantsService.setSelectedRestaurant(selectedResto);

        this.setBranches(); // after setting restaurant
      });
  }

  private setBranches(): void {
    this.restaurantsService
      .getBranches()
      .pipe(
        take(1),
        switchMap((branches) => {
          this.branches = branches;
          return this.restaurantsService.getSelectedBranchId().pipe(take(1));
        })
      )
      .subscribe((branchId) => {
        this.branchId = branchId;

        this.restaurantsService.setSelectedBranchId(branchId);
        this.emitFiltersValueChange(this.restaurantId || '', this.branchId);
      });
  }

  public onRestaurantChange(event: Event) {
    const customEvent = event as CustomEvent;
    const value = customEvent?.detail?.value || null;

    this.restaurantId = value;
    this.restaurantsService.setSelectedRestaurantId(value);
    this.restaurantsService.setSelectedBranchId(null); // clear branch on restaurant change

    this.emitFiltersValueChange(value, null);

    this.setBranches(); // fetch new branches for selected restaurant
  }

  public onBranchChange(event: Event) {
    const customEvent = event as CustomEvent;
    const value = customEvent?.detail?.value || null;

    this.emitFiltersValueChange(this.restaurantId || '', value);
    this.branchId = value;
    this.restaurantsService.setSelectedBranchId(value);
  }

  private emitFiltersValueChange(restaurantId: string, branchId: string | null): void {
   
    if(this.restaurantId) {
      localStorage.setItem('selectedRestaurantId', restaurantId);
    }

    if(branchId) {
      localStorage.setItem('selectedBranchId', branchId || '');
    }

    this.filtersValueChange.emit({
      restaurantId,
      restaurantBranchId: this.filterMode === 'restaurant' ? null : branchId,
    });
  }
  
}
